import { useCallback, useEffect, useState } from 'react'
import type { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'
import type { EmbeddedTranscriptMessage, TranscriptMessage } from './clipboard'
import invariant from 'tiny-invariant'

type Communication = BffCrmPersonChatRetrieveResponse['communications']['results'][number]

interface MessageSelectionState {
  selectedIds: Set<number>
  anchorId: number | null
}

interface UseMessageSelectionReturn {
  selectedIds: Set<number>
  isSelected: (id: number) => boolean
  toggleSelect: (id: number, isShiftKey: boolean) => void
  clearSelection: () => void
  selectedCount: number
  getSelectedMessagesForClipboard: () => TranscriptMessage[]
}

/**
 * Hook for managing chat message selection state.
 *
 * Features:
 * - Single click to toggle any message in/out of selection
 * - Shift+click for range selection (replaces current selection with the range)
 * - Escape key to clear selection
 * - Returns messages in display order for clipboard
 *
 * @param communications - List of communications to select from
 * @param meId - Current user ID for determining "mine" messages
 */
export function useMessageSelection(
  communications: Communication[],
  meId: number,
): UseMessageSelectionReturn {
  const [state, setState] = useState<MessageSelectionState>({
    selectedIds: new Set(),
    anchorId: null,
  })

  // Get indices of chat messages only (not emails or phone calls)
  const getChatMessageIndex = useCallback(
    (id: number): number => {
      return communications.findIndex(
        c => c.content_object.kind === 'chat' && c.content_object.id === id,
      )
    },
    [communications],
  )

  const toggleSelect = useCallback(
    (id: number, isShiftKey: boolean) => {
      setState(prev => {
        const targetIndex = getChatMessageIndex(id)
        if (targetIndex === -1) return prev

        const newSelectedIds = new Set(prev.selectedIds)

        if (isShiftKey && prev.anchorId !== null) {
          // Range selection: replace selection with all chat messages between anchor and target
          const anchorIndex = getChatMessageIndex(prev.anchorId)
          if (anchorIndex === -1) return prev

          const start = Math.min(anchorIndex, targetIndex)
          const end = Math.max(anchorIndex, targetIndex)

          newSelectedIds.clear()
          for (let i = start; i <= end; i++) {
            const comm = communications[i]
            if (comm?.content_object.kind === 'chat') {
              newSelectedIds.add(comm.content_object.id)
            }
          }

          return { selectedIds: newSelectedIds, anchorId: prev.anchorId }
        } else {
          // Plain click: toggle the message freely, update anchor to this message
          if (newSelectedIds.has(id)) {
            newSelectedIds.delete(id)
            return {
              selectedIds: newSelectedIds,
              anchorId: newSelectedIds.size === 0 ? null : id,
            }
          } else {
            newSelectedIds.add(id)
            return { selectedIds: newSelectedIds, anchorId: id }
          }
        }
      })
    },
    [communications, getChatMessageIndex],
  )

  const clearSelection = useCallback(() => {
    setState({ selectedIds: new Set(), anchorId: null })
  }, [])

  const isSelected = useCallback((id: number) => state.selectedIds.has(id), [state.selectedIds])

  const getSelectedMessagesForClipboard = useCallback((): TranscriptMessage[] => {
    return communications
      .filter(c => c.content_object.kind === 'chat' && state.selectedIds.has(c.content_object.id))
      .map(c => {
        const msg = c.content_object
        // Used for type narrowing, 'chat' is already filtered above
        invariant(msg.kind === 'chat', 'Expected chat message')

        let attachmentType: 'image' | 'file' | null = null
        if (msg.attachment) {
          attachmentType = 'image'
        } else if (msg.file_attachment) {
          attachmentType = 'file'
        }
        // Extract embedded quoted messages from relations
        const quotedMessages: EmbeddedTranscriptMessage[] = (msg.relations ?? []).flatMap(r => {
          const quoted = r.content_object
          if (quoted?.kind !== 'chat') return []
          return [
            {
              sender_name: quoted.sender_name ?? '',
              sent_at: quoted.sent_at ?? new Date().toISOString(),
              text: quoted.text ?? '',
              case_id: quoted.case_id ?? null,
            },
          ]
        })
        return {
          object_id: msg.id,
          content_type: 'chatmessagev2' as const,
          sender_name: msg.sender.full_name_used,
          sent_at: msg.sent_at ?? new Date().toISOString(),
          text: msg.text ?? '',
          is_mine: msg.sender.id === meId,
          avatar_url: msg.sender.avatar_url,
          attachment_type: attachmentType,
          quoted_messages: quotedMessages,
        }
      })
  }, [communications, state.selectedIds, meId])

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state.selectedIds.size > 0) {
        clearSelection()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [state.selectedIds.size, clearSelection])

  return {
    selectedIds: state.selectedIds,
    isSelected,
    toggleSelect,
    clearSelection,
    selectedCount: state.selectedIds.size,
    getSelectedMessagesForClipboard,
  }
}
